import { getVamoosInitScript, VamoosInterface } from "./vamoosSetup";
import { extractFilesFromZip, fetchZipFile } from "../../helpers";
import { getEwaData, setEwaData } from "../api";
import { EWA_CSS_VARIABLE_NAMES } from "../../helpers/constants";
import * as esbuild from "esbuild-wasm";

export const installPostMessageType = "vamoos-install-bridge-request";
export const htmlPostMessageType = "vamoos-html-bridge-request";

const findFileInZip = async (zip, filename) => {
  for (const [path, file] of Object.entries(zip.files)) {
    if (!file.dir && path.endsWith(`${filename}`)) {
      return await file.async("string");
    }
  }
  return null;
};

const createMessageHandler = (itinId, iframe, postMessageType) => {
  return async (event) => {
    if (!event.data || event.data.type !== postMessageType) return;

    const { method, args, callId } = event.data;

    // Each iframe may receive all postMessage events.
    // Use the ewaId encoded in callId to ensure only the matching iframe processes its message.
    const [ewaIdFromCallId] = callId.split("|");
    const iframeEwaId = iframe.dataset.ewaId;
    if (ewaIdFromCallId !== iframeEwaId) return;

    let result, error = null;

    try {
      switch (method) {
        case "getCurrentUser":
          result = VamoosInterface.getCurrentUser(...args);
          break;
        case "getItinerary":
          result = VamoosInterface.getItinerary(...args);
          break;
        case "getData":
          result = VamoosInterface.getData(...args);
          break;
        case "openDocument":
          VamoosInterface.openDocument(...args);
          break;
        case "setScreenTitle":
          VamoosInterface.setScreenTitle(...args);
          break;
        case "set":
          result = await VamoosInterface.set(...args);
          break;
        case "get":
          result = await VamoosInterface.get(...args);
          break;
        case "remove":
          result = await VamoosInterface.remove(...args);
          break;

        default:
          throw new Error(`Unknown method: ${method}`);
      }
    } catch (err) {
      error = err.message || String(err);
    }

    // Special handling for openDocument: it may cause the iframe to navigate/unmount,
    // so we resolve the promise immediately to avoid hanging, while still sending the postMessage.
    if (!iframe?.contentWindow || method === "openDocument") return;

    // Send the result or error back to the iframe
    iframe.contentWindow.postMessage({
      type: "vamoos-bridge-response",
      callId,
      result,
      error,
    }, "*");
  };
};

// Helper to register the dynamically created handler.
export const registerMessageHandler = (itinId, iframe, postMessageType) => {
  const handler = createMessageHandler(itinId, iframe, postMessageType);

  window.removeEventListener("message", handler);
  window.addEventListener("message", handler);
  return () => {
    window.removeEventListener("message", handler);
  };
};

export const inlineImports = async (zip, jsContent, processedFiles = new Set(), basePath = "", insideAsync = false) => {
  // Determine if a given match is inside an async function or an await import
  const isInsideAsyncScope = (content, matchTextOrIndex) => {
    const matchIndex = typeof matchTextOrIndex === "number"
      ? matchTextOrIndex
      : content.indexOf(matchTextOrIndex);
    if (matchIndex === -1) return false;

    const before = content.slice(0, matchIndex);

    // Look for "async function" or "async () =>" before the match
    const asyncFuncRegex = /\basync\s+function\b|\basync\s*\([\s\S]*?\)\s*=>/;

    // Check if any async scope appears before the match
    if (asyncFuncRegex.test(before)) return true;

    // Also check if the match itself is an await import (already async context)
    if (/await\s+import\s*\(/.test(matchTextOrIndex)) return true;

    return false;
  };

  // Check if a line containing the match is commented out
  const isCommentedOut = (content, matchIndex) => {
    const before = content.slice(0, matchIndex);
    const lastLine = before.split("\n").pop();
    // Line comments or block comment start
    return /^\s*\/\//.test(lastLine) || /\/\*/.test(lastLine);
  };

  const dirname = (path) => path.split("/").slice(0, -1).join("/");

  // Resolve relative path with .. and . segments
  const normalisePath = (base, relative) => {
    const parts = (base ? `${base}/${relative}` : relative).split("/");
    const stack = [];
    for (const part of parts) {
      if (part === "..") stack.pop();
      else if (part !== ".") stack.push(part);
    }
    return stack.join("/");
  };

  let inlinedContent = jsContent;

  const processImports = async (regex, replacer) => {
    for (const match of [...inlinedContent.matchAll(regex)]) {
      if (isCommentedOut(inlinedContent, match.index)) continue;

      const importPath = match[1];
      // Only inline local imports (relative paths)
      if (!importPath?.startsWith(".")) continue;

      const normalisedFinalPath = normalisePath(basePath, importPath.replace(/^\.\/+/, ""));
      if (processedFiles.has(normalisedFinalPath) && !insideAsync) {
        inlinedContent = inlinedContent.replace(match[0], "");
        continue;
      }

      processedFiles.add(normalisedFinalPath);
      const importedCode = await findFileInZip(zip, normalisedFinalPath);
      if (!importedCode) continue;

      // Remove export keywords to inline code
      const strippedExports = importedCode
        .replace(/export\s+(async\s+)?function\s+/g, "$1function ")
        .replace(/export\s+(const|let|var|class)\s+/g, "$1 ")
        .replace(/export\s+default\s+/g, `const __defaultExport_${normalisedFinalPath.replace(/\W/g, "_")} = `);

      const newBase = normalisedFinalPath.includes("/") ? dirname(normalisedFinalPath) : "";

      // Recursively inline imports inside the imported code
      const nested = await inlineImports(zip, strippedExports, processedFiles, newBase, isInsideAsyncScope(inlinedContent, match[0]));
      inlinedContent = inlinedContent.replace(match[0], replacer(nested));
    }
  };

  // Process static imports: import ... from '...';
  await processImports(/import\s+[\s\S]+?\s+from\s+["'](.+?)["'];?/g, nested => nested);

  // Process awaited dynamic imports: await import("...");
  await processImports(/await\s+import\s*\(\s*["'](.+?)["']\s*\)/g, nested => {
    const exportNames = [...nested.matchAll(/^(?:export\s+)?(?:async\s+)?function\s+(\w+)|^(?:export\s+)?(?:const|let|var|class)\s+(\w+)/gm)]
      .map(m => m[1] || m[2]);

    // If nested contains top-level await, keep async wrapper, else plain IIFE
    const needsAsync = /await\s+/.test(nested);
    const wrapper = needsAsync ? "async()=>{" : "()=>{";

    const res = needsAsync ? `await (${wrapper}${nested}${exportNames.length ? ` return { ${exportNames.join(", ")} };` : ""}})()`
      : `(${wrapper}${nested}${exportNames.length ? ` return { ${exportNames.join(", ")} };` : ""}})()`;
    return res
  });

  // Process non-awaited dynamic imports: import("...");
  await processImports(/(?<!await\s)(?<!from\s*)\bimport\s*\(\s*['"](.+?)['"]\s*\)/g, nested => {
    const exportNames = [...nested.matchAll(
      /^(?:export\s+)?(?:async\s+)?function\s+(\w+)|^(?:export\s+)?(?:const|let|var|class)\s+(\w+)/gm
    )].map(m => m[1] || m[2]);

    const needsAsync = /await\s+/.test(nested);
    const wrapper = needsAsync ? "async()=>{" : "()=>{";
    const iife = `(${wrapper}${nested}${exportNames.length ? ` return { ${exportNames.join(", ")} };` : ""}})()`;

    // Wrap in a resolved Promise if async
    return needsAsync
      ? `(()=>Promise.resolve(${iife}))()`
      : iife;
  });

  return inlinedContent;
};

export const bundleZipJs = async (jSFiles, entryPoints) => {
  const jsFiles = {};
  for (const f of jSFiles) {
    if (f.path.endsWith(".js")) {
      jsFiles[f.path.replace(/^\.?\//, "")] = await f.blob.text();
    }
  }
  if (!Object.keys(jsFiles).length) return null;

  const normalizePath = (p) => {
    return p.replace(/^\.?\/+/, ""); // removes ./ and / at start
  }

  const plugin = {
    name: "in-memory",
    setup(build) {
      build.onResolve({ filter: /.*/ }, (args) => {
        const p = normalizePath(args.path);

        if (p in jsFiles) {
          // If the file exists in the zip, use the "zip" namespace
          return { path: p, namespace: "zip" };
        }

        // If a relative import is missing, throw an error
        if (args.path.startsWith(".")) {
          return { errors: [{ text: `Missing JS file in zip: ${args.path}` }] };
        }

        // Otherwise, allow it to be treated as external (e.g., CDN imports)
        return { external: true };
      });


      build.onLoad({ filter: /.*/, namespace: "zip" }, (args) => {
        return {
          contents: jsFiles[args.path],
          loader: "js",
        };
      });
    }
  };

  const result = await esbuild.build({
    entryPoints: entryPoints,
    bundle: true,
    splitting: false, // keep bundle in one file (no extra chunk URLs)
    write: false, // do not write to disk
    format: "esm", // output as ES module
    plugins: [plugin],
  });

  return result.outputFiles[0].text;
};

const buildBundleForInstallJs = async (extractedFiles) => {
  const validFiles = getValidFiles(extractedFiles, false);

  const scriptFiles = validFiles.filter(({ path }) => [".js", ".mjs", ".cjs"].some(ext => path.endsWith(ext)));
  if (scriptFiles.length <= 1) return null;

  let sharedRootFolder = null;
  const firstPath = scriptFiles[0].path;
  const segments = firstPath.split('/');
  if (segments.length > 1) {
    const candidate = segments[0];
    const allHave = scriptFiles.every(({ path }) => path.startsWith(candidate + '/'));
    if (allHave) sharedRootFolder = candidate;
  }

  const normalizedScriptFiles = scriptFiles.map(file => {
    const clean = file.path.replace(/^\.?\/+/, "");
    if (sharedRootFolder && clean.startsWith(sharedRootFolder + "/")) {
      return { ...file, path: clean.substring(sharedRootFolder.length + 1) };
    }
    return { ...file, path: clean };
  });

  const entryPoint = normalizedScriptFiles.find(({ path }) => path.endsWith("install.js")).path;
  const bundledScriptCode = await bundleZipJs(normalizedScriptFiles, [entryPoint]);
  if (
    !bundledScriptCode ||
    bundledScriptCode.trim() === "(() => {\n})();\n" ||
    bundledScriptCode.trim() === "(() => {})();"
  ) {
    return null;
  }
  return bundledScriptCode;
}

export const loadEwa = async (zipUrl, iframe, itinId, postMessageType) => {
  try {
    const zip = await fetchZipFile(zipUrl);

    const installJs = await findFileInZip(zip, "install.js");
    if (!installJs) return null;

    let inlinedInstallJs = installJs;
    if (
      /\bimport\s*\(\s*['"`]/.test(installJs) || // dynamic imports (with or without await)
      /\bimport\s+[\w{}*\s,]+\s+from\s*['"`]/.test(installJs) // static imports
    ) {
      const extractedFiles = await extractFilesFromZip(zip);
      const bundledScriptCode = await buildBundleForInstallJs(extractedFiles);
      if (bundledScriptCode) inlinedInstallJs = bundledScriptCode;

      if (!bundledScriptCode && (/await\s+import\s*\(\s*['"`]/.test(installJs) || /import\s+[\w{*][^'"]*from\s*['"]/.test(installJs))) {
        inlinedInstallJs = await inlineImports(zip, installJs);
      }
    }

    iframe.contentWindow.postMessage({ type: "injectCode", untrustedCode: inlinedInstallJs }, "*");

    // Parent listens for function calls from the iframe
    const cleanup = registerMessageHandler(itinId, iframe, postMessageType);
    // Return a cleanup function so the caller can remove the listener
    return cleanup;
  } catch (error) {
    // eslint-disable-next-line no-throw-literal
    throw { modalMessage: error.message };
  }
};

export const getValidFiles = (extractedFiles, excludeInstall = true) => {
  return extractedFiles.filter(({ path }) => {
    return (
      !path.endsWith('/') &&            // skip folders
      !path.includes('__MACOSX') &&     // skip macOS metadata
      !path.includes('.DS_Store') &&    // skip macOS system files
      !path.endsWith('.ini') &&         // skip config files
      !path.endsWith('.tmp') &&         // skip temp files
      !path.endsWith('Thumbs.db') &&   // skip Windows thumbnail cache
      (!excludeInstall || !path.includes("install.js"))     // skip installer scripts
    );
  });
};

export const createSandboxedIframe = (trusted, shouldNotifyParent, docId, postMessageType, itinId) => {
  const iframe = document.createElement("iframe");
  if (!trusted) iframe.setAttribute("sandbox", "allow-scripts");
  iframe.style.display = "none";
  iframe.dataset.ewaId = docId;

  const storagePrefix = `${itinId}_${docId}_`;
  const lsPatcherScript = trusted ? createStoragePatcherScript("localStorage", storagePrefix) : createUntrustedStorageScript("localStorage", storagePrefix);
  const ssPatcherScript = trusted ? createStoragePatcherScript("sessionStorage", storagePrefix) : createUntrustedStorageScript("sessionStorage", storagePrefix);
  const vamoosScript = getVamoosInitScript(trusted, shouldNotifyParent, docId, postMessageType);

  iframe.srcdoc = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="utf-8"/>
    <title>"Sandboxed install Iframe"</title>
    <script>
        ${lsPatcherScript}
    </script>
    <script>
        ${ssPatcherScript}
    </script>
</head>
<body>
    <script>
        ${vamoosScript}
    </script>
</body>
</html>
 `;

  return iframe;
};

export const postMessageToIframe = (iframe, type, event) => {
  if (!iframe?.contentWindow) return;

  iframe.contentWindow.postMessage(
    { type, event },
    "*"
  );
};

const syncData = async (ref, dataScopeId, payload, t) => {
  try {
    const { res, data, error } = await setEwaData(ref, dataScopeId, payload);
    if (!res.ok || error) {
      // eslint-disable-next-line no-throw-literal
      throw { modalMessage: error || data?.error[0]?.message || data?.error || t("auth_error_get_profile_info") };
    }
  } catch (err) {
    // eslint-disable-next-line no-throw-literal
    throw { modalMessage: err || t("auth_error_get_profile_info") };
  }
};

export const syncEwaData = async (groupedLocalData, t) => {
  for (const vamoosId in groupedLocalData) {
    const localValue = groupedLocalData[vamoosId];

    for (const dataScopeId in localValue) {
      if (dataScopeId === "ref") continue;
      const localItem = localValue[dataScopeId];

      let serverItem;
      try {
        const { res, data: serverItem, error } = await getEwaData(localValue.ref, dataScopeId);
        if (!res.ok || error) {
          // eslint-disable-next-line no-throw-literal
          throw { modalMessage: error || serverItem?.error[0]?.message || serverItem?.error || t("auth_error_get_profile_info") };
        }
      } catch (err) {
        // eslint-disable-next-line no-throw-literal
        throw { modalMessage: err || t("auth_error_get_profile_info") };
      }

      const localUpdatedAt = localItem?.updatedAt;
      const serverUpdatedAt = serverItem?.updatedAt;

      const shouldSync =
        !serverItem || // no server login found, must sync
        (!serverUpdatedAt || (localUpdatedAt && new Date(localUpdatedAt) > new Date(serverUpdatedAt)));

      if (shouldSync) {
        const { updatedAt, ...rest } = localItem;
        const payload = {
          data: { ...rest },
        };
        await syncData(localValue.ref, dataScopeId, payload, t);
      }
    }
  }
};

export const collectLocalEwaData = (allItems = []) => {
  const grouped = {};

  for (const { vamoosId, dataScopeId, key, value, updatedAt, ref } of allItems) {
    if (!grouped[vamoosId]) grouped[vamoosId] = {};
    if (!grouped[vamoosId][dataScopeId]) grouped[vamoosId][dataScopeId] = { __latestUpdatedAt: null };

    grouped[vamoosId][dataScopeId][key] = value;

    const prev = grouped[vamoosId][dataScopeId].__latestUpdatedAt;
    if (!prev || new Date(updatedAt) > new Date(prev)) {
      grouped[vamoosId][dataScopeId].__latestUpdatedAt = updatedAt;
    }
    grouped[vamoosId].ref = ref;
  }

  for (const vamoosId in grouped) {
    for (const dataScopeId in grouped[vamoosId]) {
      if (dataScopeId === "ref") continue;

      const scopeData = grouped[vamoosId][dataScopeId];
      scopeData.updatedAt = scopeData.__latestUpdatedAt;
      delete scopeData.__latestUpdatedAt;
    }
  }

  return grouped;
};

export const getEwaBackPath = () => {
  try {
    const stored = localStorage.getItem("ewaBackPath");
    return stored ? JSON.parse(stored) : null;
  } catch {
    localStorage.removeItem("ewaBackPath");
    return null;
  }
};

const imgReplaceScript = `
(function() {
  function replaceImages(map) {
    const imgs = Array.from(document.querySelectorAll("img"));
    if (imgs.length === 0) return;

    imgs.forEach(img => {
      const src = img.getAttribute("src");
      if (!src) return;

      // try direct match first
      if (map[src]) {
        img.setAttribute("src", map[src]);
        return;
      }

      // fallback: try matching by filename (last segment)
      const filename = src.split("/").pop();
      for (const key in map) {
        if (key.endsWith(filename)) { 
          img.setAttribute("src", map[key]);
          break;
        }
      }
    });
  }

  window.addEventListener("message", (event) => {
    if (event.data?.type === "ewa-replace-all-image-urls") {
    setTimeout(() => {
      replaceImages(event.data.map);
    }, event.data.delay);
    }
  });
})();
`;

const fetchInterceptorScript = `
(function() {
  const originalFetch = window.fetch;

  window.fetch = async function(input, init) {
    try {
      let url = (typeof input === "string") ? input : input.url;
      
      const response = await originalFetch.apply(this, arguments);

      window.parent.postMessage({
        type: "ewa-fetch-request",
        url: url,
      }, "*");
      
      return response;
    } catch (err) {
      console.warn("Fetch interceptor error:", err);
    }
  };
})();
`;

const imageInterceptorScript = `
(function() {
  const pathToDataUrlMap = {};
  let hasRequestedMap = false;
  
  const normalizePath = (path) => path.replace(/^(\\.\\/|\\/)+/, "");

  const getDataUrl = (src) => {
    if (!src) return null;

    const normSrc = normalizePath(src);
    if (pathToDataUrlMap[normSrc]) return pathToDataUrlMap[normSrc];

    // Match by last segment(s)
    const srcParts = normSrc.split('/');
    for (const key in pathToDataUrlMap) {
      const keyParts = key.split('/');
      // Compare only last N segments (min length of both arrays)
      const len = Math.min(srcParts.length, keyParts.length);
      const srcTail = srcParts.slice(-len).join('/');
      const keyTail = keyParts.slice(-len).join('/');
      if (srcTail === keyTail) return pathToDataUrlMap[key];
    }

    return null;
  };
  
  // Intercept new images by patching Image.src
  const originalSrcDescriptor = Object.getOwnPropertyDescriptor(Image.prototype, 'src');
  Object.defineProperty(Image.prototype, 'src', {
    configurable: originalSrcDescriptor.configurable,
    enumerable: originalSrcDescriptor.enumerable,
    set(value) {
      const dataUrl = getDataUrl(value);
      if (dataUrl) value = dataUrl;
      originalSrcDescriptor.set.call(this, value);
    },
    get() {
      return originalSrcDescriptor.get.call(this);
    }
  });
  
  window.addEventListener('message', (event) => {
    if (event.data?.type === 'ewa-replace-all-image-urls' && event.data.map) {
      Object.assign(pathToDataUrlMap, event.data.map);
      hasRequestedMap = true;
    }
  });

  // Notify parent we are ready for mapping
  if (window.parent && window.parent !== window && !hasRequestedMap) {
    window.parent.postMessage({ type: 'ewa-refresh-images' }, '*');
  }
})();
`;

const linkClickInterceptorScript = `
            document.addEventListener('click', function(event) {
              try {
                const anchor = event.target.closest('a[href]');
                if (!anchor) return;
            
                const href = anchor.getAttribute('href');
                if (!href) return;
                             
                 // Handle PDF links
                const isDataPdfUri = href.startsWith('data:application/pdf;');
  
                if (isDataPdfUri || href.endsWith('.pdf')) {
                  event.preventDefault();
            
                  // Send to parent
                  window.parent.postMessage({
                    type: 'ewa-pdf-click',
                    url: href,
                    source: isDataPdfUri ? "assets" : "link",
                  }, '*');
                }
                // Handle http(s) links - force target _self
                if (href.match(/^https?:\\/\\//)) {
                 anchor.setAttribute('target', '_self');
                  return;
                }
                
                if (!href || href.startsWith('#') || href.startsWith('mailto:')) return;
                
                 event.preventDefault();
                 window.parent.postMessage({
                  type: 'ewa-navigation-link-click',
                  path: href,
                }, '*');
              } catch (e) {
                console.error('Link interceptor error:', e);
              }
            });
        `;

export const ewaNavigationListenerScript = `
(function() {
  const notifyParent = (state, url) => {
    window.parent.postMessage({
      type: 'ewa-pushstate-navigation',
      state: state ?? null,
      url: url,
    }, '*');
  };

  // Hook into pushState only
  const _pushState = history.pushState;
  history.pushState = function(state, title, url) {
    _pushState.call(this, state, title, url);
    notifyParent(state, url?.toString?.());
  };
})();
`;

const createStoragePatcherScript = (storageName, prefix) => {
  return `
(function() {
    const KEY_PREFIX = '${prefix}_';
  const originalStorage = window.${storageName};

  if (!originalStorage) {
    console.error('${storageName} not available');
    return;
  }

  const isPrefixed = (k) => typeof k === 'string' && k.startsWith(KEY_PREFIX);
  const prefixKey = (k) => KEY_PREFIX + String(k);
  const unprefixKey = (pk) => (isPrefixed(pk) ? pk.slice(KEY_PREFIX.length) : null);

  const originalGetItem = originalStorage.getItem.bind(originalStorage);
  const originalSetItem = originalStorage.setItem.bind(originalStorage);
  const originalRemoveItem = originalStorage.removeItem.bind(originalStorage);
  const originalKey = originalStorage.key.bind(originalStorage);
  const lengthDescriptor = Object.getOwnPropertyDescriptor(Storage.prototype, 'length');

  function getAllPrefixedKeys() {
    const result = [];
    const nativeLen = lengthDescriptor && lengthDescriptor.get ? lengthDescriptor.get.call(originalStorage) : originalStorage.length;
    for (let i = 0; i < nativeLen; i++) {
      const k = originalKey(i);
      if (k != null && isPrefixed(k)) result.push(k);
    }
    return result;
  }

  const handler = {
    get(target, prop) {
      if (prop === 'getItem') {
        return (key) => key == null ? null : originalGetItem(prefixKey(key));
      }
      if (prop === 'setItem') {
        return (key, value) => {
          if (value === undefined) throw new TypeError("Value cannot be undefined");
          originalSetItem(prefixKey(key), String(value));
        };
      }
      if (prop === 'removeItem') {
        return (key) => originalRemoveItem(prefixKey(key));
      }
      if (prop === 'clear') {
        return () => {
          for (const k of getAllPrefixedKeys()) originalRemoveItem(k);
        };
      }
      if (prop === 'key') {
        return (index) => {
          const keys = getAllPrefixedKeys().map(unprefixKey);
          return keys[index] ?? null;
        };
      }
      if (prop === 'length') {
        return getAllPrefixedKeys().length;
      }
      if (typeof prop === 'string' && /^[0-9]+$/.test(prop)) {
        const idx = Number(prop);
        const keys = getAllPrefixedKeys().map(unprefixKey);
        const k = keys[idx];
        return k !== undefined ? originalGetItem(prefixKey(k)) : undefined;
      }
      if (typeof prop === 'string' && !(prop in Object.prototype)) {
        return originalGetItem(prefixKey(prop));
      }
      const val = originalStorage[prop];
      return typeof val === 'function' ? val.bind(originalStorage) : val;
    },

    set(target, prop, value) {
      if (typeof prop === 'string' && prop !== 'length' && prop !== 'key') {
        if (value === undefined) throw new TypeError("Value cannot be undefined");
        originalSetItem(prefixKey(prop), String(value));
        return true;
      }
      target[prop] = value;
      return true;
    },

    deleteProperty(target, prop) {
      if (typeof prop === 'string') {
        originalRemoveItem(prefixKey(prop));
        return true;
      }
      return false;
    },

    ownKeys() {
      return getAllPrefixedKeys().map(unprefixKey);
    },

    getOwnPropertyDescriptor(target, prop) {
      if (typeof prop === 'string') {
        const unprefixedKeys = getAllPrefixedKeys().map(unprefixKey);
        if (unprefixedKeys.includes(prop)) {
          return {
            configurable: true,
            enumerable: true,
            value: originalGetItem(prefixKey(prop)),
            writable: true
          };
        }
      }
      return Object.getOwnPropertyDescriptor(originalStorage, prop) || undefined;
    }
  };

  const proxy = new Proxy(originalStorage, handler);

  Object.defineProperty(window, '${storageName}', {
    configurable: true,
    enumerable: true,
    get() { return proxy; }
  });

})();`;
};

export function createUntrustedStorageScript(storageName, prefix) {
  return `
    (function() {
      const storageType = ${JSON.stringify(storageName)};
      const prefix = ${JSON.stringify(prefix)} + "_";
      const cache = {};
      const pending = new Map();

      window.addEventListener("message", (event) => {
        const data = event.data;
        if (!data || typeof data !== "object") return;

        if (data.type === "ewa-storage-update" && data.storage === storageType && data.prefix === prefix) {
          const fullKey = prefix + data.key;
          switch (data.op) {
            case "set":
              cache[fullKey] = data.value;
              break;
            case "remove":
              delete cache[fullKey];
              break;
            case "clear":
              for (const k of Object.keys(cache)) {
                if (k.startsWith(prefix)) delete cache[k];
              }
              break;
          }
        }

        if (data.type === "storage-response" && data.callId) {
          const resolver = pending.get(data.callId);
          if (resolver) {
            pending.delete(data.callId);
            resolver(data.value);
          }
        }
      });
      
      const storageProxy = new Proxy({}, {
        get(_, key) {
          if (key === "getItem") return (k) => cache[prefix + k] ?? null;
          if (key === "setItem") return (k, v) => {
            const fullKey = prefix + k;
            cache[fullKey] = v;
            window.parent.postMessage({ type: "ewa-storage-set", storage: storageType, key: k, value: v, prefix }, "*");
          };
          if (key === "removeItem") return (k) => {
            const fullKey = prefix + k;
            delete cache[fullKey];
            window.parent.postMessage({ type: "ewa-storage-remove", storage: storageType, key: k, prefix }, "*");
          };
          if (key === "clear") return () => {
            for (const k of Object.keys(cache)) {
              if (k.startsWith(prefix)) delete cache[k];
            }
            window.parent.postMessage({ type: "ewa-storage-clear", storage: storageType, prefix }, "*");
          };
          if (key === "key") return (index) => {
            const keys = Object.keys(cache)
              .filter(k => k.startsWith(prefix))
              .map(k => k.slice(prefix.length));
            return keys[index] ?? null;
          };
          if (key === "length") {
            return Object.keys(cache).filter(k => k.startsWith(prefix)).length;
          }
      
          // default: property access like localStorage.theme
          return cache[prefix + key] ?? null;
        },
        set(_, key, value) {
          const fullKey = prefix + key;
          cache[fullKey] = value;
          window.parent.postMessage({ type: "ewa-storage-direct-set", storage: storageType, key, value, prefix }, "*");
          return true;
        },
        deleteProperty(_, key) {
          const fullKey = prefix + key;
          delete cache[fullKey];
          window.parent.postMessage({ type: "ewa-storage-direct-delete", storage: storageType, key, prefix }, "*");
          return true;
        },

        ownKeys() {
          return Object.keys(cache)
            .filter(k => k.startsWith(prefix))
            .map(k => k.slice(prefix.length));
        },
      
        getOwnPropertyDescriptor(_, key) {
          if (cache[prefix + key] !== undefined) {
            return { enumerable: true, configurable: true };
          }
          return undefined;
        }
      });
      
      function requestInitialCache() {
       const callId = storageType + "init_" + Date.now() + "_" + Math.random().toString(36).slice(2);
        return new Promise(resolve => {
          pending.set(callId, (snapshot) => {
            if (snapshot && typeof snapshot === "object") {
              for (const [key, value] of Object.entries(snapshot)) {
                cache[prefix + key] = value;
              }
            }
            resolve();
          });
          window.parent.postMessage({ type: "ewa-storage-init", storage: storageType, prefix, callId }, "*");
        });
      }

      Object.defineProperty(window, storageType, {
        value: storageProxy,
        configurable: true
      });
      
      requestInitialCache();
    })();
  `;
}

export const injectRootStyles = (doc, variableNames = []) => {
  const global = document.documentElement;

  const variables = variableNames.reduce((acc, name) => {
    const value = getComputedStyle(global).getPropertyValue(name).trim();
    if (value) {
      const prefixedName = name.replace(/^--/, '--vamoos-');
      acc[prefixedName] = value;
    }
    return acc;
  }, {});

  if (!Object.keys(variables).length) return;

  const cssVars = Object.entries(variables)
    .map(([key, value]) => `${key}: ${value};`)
    .join('\n');

  const styleTag = doc.createElement('style');
  styleTag.id = 'vamoos-root-styles';
  styleTag.textContent = `:root { ${cssVars} }`;

  doc.head.appendChild(styleTag);
};

export const injectScriptsAndStyles = (doc, trusted, itemId, storagePrefix, bundleDataUrl) => {
  const vamoosScript = getVamoosInitScript(trusted, false, itemId, htmlPostMessageType);
  const lsPatcherScript = trusted ? createStoragePatcherScript("localStorage", storagePrefix) : createUntrustedStorageScript("localStorage", storagePrefix);
  const ssPatcherScript = trusted ? createStoragePatcherScript("sessionStorage", storagePrefix) : createUntrustedStorageScript("sessionStorage", storagePrefix);

  if (bundleDataUrl) {
    const allScripts = [...doc.querySelectorAll("script")];
    allScripts.forEach(script => script.remove());
  }

  // head injection
  const head = doc.querySelector('head') || doc.createElement('head');
  if (!doc.querySelector('head')) {
    // insert <head> before <body> if missing
    const body = doc.querySelector('body');
    if (body) {
      doc.documentElement.insertBefore(head, body);
    } else {
      doc.documentElement.appendChild(head);
    }
  }

  const lsPatchScriptEl = doc.createElement('script');
  lsPatchScriptEl.textContent = lsPatcherScript;
  head.appendChild(lsPatchScriptEl);

  const ssPatchScriptEl = doc.createElement('script');
  ssPatchScriptEl.textContent = ssPatcherScript;
  head.appendChild(ssPatchScriptEl);

  const headScriptEl = doc.createElement('script');
  headScriptEl.textContent = vamoosScript;
  head.appendChild(headScriptEl);

  // body injection
  const body = doc.querySelector('body') || doc.createElement('body');
  if (!doc.querySelector('body')) doc.documentElement.appendChild(body);

  const bodyScriptEl = doc.createElement('script');
  bodyScriptEl.textContent = linkClickInterceptorScript;
  body.appendChild(bodyScriptEl);

  const bodyScriptEl2 = doc.createElement('script');
  bodyScriptEl2.textContent = fetchInterceptorScript;
  body.appendChild(bodyScriptEl2);

  const bodyScriptEl3 = doc.createElement('script');
  bodyScriptEl3.textContent = imgReplaceScript;
  body.appendChild(bodyScriptEl3);

  const bodyScriptEl4 = doc.createElement("script");
  bodyScriptEl4.textContent = ewaNavigationListenerScript;
  body.appendChild(bodyScriptEl4);

  const bodyScriptEl5 = doc.createElement("script");
  bodyScriptEl5.textContent = imageInterceptorScript;
  body.appendChild(bodyScriptEl5);

  injectRootStyles(doc, EWA_CSS_VARIABLE_NAMES);

  if (bundleDataUrl) {
    const bundleScript = doc.createElement("script");
    bundleScript.src = bundleDataUrl;
    body.appendChild(bundleScript);
  }

  return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
};